import { useCallback, useEffect, useState } from "react";
import { Loader2, MapPin, Plus, Share2, Trash2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DIRECTIONS = [["both", "Both ways"], ["to_airport", "To airport"], ["from_airport", "From airport"]];
const blank = { label: "", origin: "", airport: "", direction: "both", vehicle_id: "", price: "" };

const field = "h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold";

export const FlatRates = ({ vehicles = [], handle = "" }) => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api.get("/flat-rates").then(({ data }) => setRows(data.flat_rates)).catch((e) => setError(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, note) => {
    setBusy(true); setError(""); setMsg("");
    try { await fn(); load(); setMsg(note); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const vehicleName = (id) => vehicles.find((v) => v.id === id)?.name || "Every vehicle";

  return (
    <div data-testid="flat-rates-panel" className="space-y-5">
      <p className="text-sm text-slate-400">
        A fixed price for a known run — “Sugar Land → IAH = $150”. Matching trips are priced at the
        flat rate; everything else falls back to your mileage or hourly rates.
      </p>
      {error && <p data-testid="flat-rates-error" className="text-sm text-red-400">{error}</p>}
      {msg && <p data-testid="flat-rates-msg" className="text-sm text-emerald-400">{msg}</p>}

      <div className="space-y-2" data-testid="flat-rates-list">
        {rows.length === 0 && (
          <p data-testid="flat-rates-empty" className="text-sm text-slate-500">
            No flat rates yet — add your busiest airport run below.
          </p>
        )}
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-900/50 p-4">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm text-slate-100">
                <MapPin className="h-3.5 w-3.5 text-gold" />
                {r.origin} → {r.airport}
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {DIRECTIONS.find(([k]) => k === r.direction)?.[1]}
                </span>
              </p>
              <p className="mt-1 font-mono text-[11px] text-slate-500">
                {vehicleName(r.vehicle_id)}{r.label ? ` · ${r.label}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span data-testid={`flat-rate-price-${r.id}`} className="font-display text-lg text-slate-50">{money(r.price)}</span>
              <Button data-testid={`flat-rate-share-${r.id}`} size="sm" variant="outline"
                onClick={() => {
                  const url = `${window.location.origin}/route/${handle || ""}/${r.slug}`;
                  navigator.clipboard?.writeText(url);
                  setMsg("Route page link copied — text it or post it anywhere.");
                }}
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                <Share2 className="h-4 w-4" />
              </Button>
              <Button data-testid={`flat-rate-delete-${r.id}`} size="sm" disabled={busy}
                onClick={() => act(() => api.delete(`/flat-rates/${r.id}`), "Flat rate removed.")}
                className="bg-red-500/20 text-red-100 hover:bg-red-500/30">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Add a flat rate</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input data-testid="flat-rate-origin" placeholder="Pickup zone or city (e.g. Sugar Land)"
            value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className={field} />
          <Input data-testid="flat-rate-airport" placeholder="Airport keyword (e.g. IAH or Intercontinental)"
            value={form.airport} onChange={(e) => setForm({ ...form, airport: e.target.value })} className={field} />
          <Input data-testid="flat-rate-amount" type="number" step="0.01" placeholder="Flat price"
            value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} />
          <select data-testid="flat-rate-vehicle" value={form.vehicle_id}
            onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
            className="h-11 rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold">
            <option value="">Every vehicle</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {DIRECTIONS.map(([k, l]) => (
            <button key={k} type="button" data-testid={`flat-rate-direction-${k}`}
              onClick={() => setForm({ ...form, direction: k })}
              className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
                form.direction === k ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
              {l}
            </button>
          ))}
        </div>
        <Button data-testid="flat-rate-save" className="mt-4 bg-gold text-ink-900 hover:bg-gold-light"
          disabled={busy || !form.origin.trim() || !form.airport.trim() || !Number(form.price)}
          onClick={() => act(async () => {
            await api.post("/flat-rates", { ...form, price: Number(form.price) });
            setForm(blank);
          }, "Flat rate added.")}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-2 h-4 w-4" /> Add flat rate</>}
        </Button>
      </div>
    </div>
  );
};
